/**
 * Pin a frame to the desktop and back (plan 12 §1, §5B). Any window may ask;
 * only the owner overlay writes the pin records and talks to Rust, so a
 * mirror overlay or a pin window sends its request to the owner as a Tauri
 * event. The owner's windows follow the records (`desktop-pin-owner.ts`).
 */
import { emit } from "@tauri-apps/api/event";
import type { FrameType } from "@/api/spaces";
import {
  WIDGET_SIZES,
  type WidgetSize,
} from "@/components/shapes/shared/widget-face";
import {
  pinStartSize,
  widgetSizingFor,
} from "@/components/shapes/shared/widget-sizing";
import { wallFrame } from "@/components/wall/wall-frame-source";
import { openFrameInProject } from "@/components/layout/cross-project-navigation";
import { getFloatingFrameDisplayArea } from "@/desktop/floating-frame-displays";
import {
  closeFloatingFrame,
  useFloatingFrameStore,
} from "@/desktop/floating-frame-store";
import { useActiveDisplay } from "@/desktop/use-active-display";
import { getWindowOrigin } from "@/desktop/use-window-origin";
import {
  getWindowDisplayId,
  getWindowRoleSync,
} from "@/desktop/use-window-role";
import { isTauri } from "@/lib/is-tauri";
import { leavePreview } from "@/lib/frame-preview-removal";
import { usePresentationStore } from "@/stores/presentation-store";
import { useProjectStore } from "@/stores/project-store";
import { foreignWallFrame } from "@/stores/wall-scope-store";
import {
  DESKTOP_PIN_EVENTS,
  desktopOccupiedRects,
  type DesktopPinIdPayload,
} from "./desktop-pin-contract";
import { pinDisplayIdAt, pinWorkArea } from "./desktop-pin-displays";
import { firstFreeSpot } from "./desktop-pin-placement";
import {
  desktopPinFor,
  putDesktopPin,
  readDesktopPins,
  removeDesktopPin,
  subscribeDesktopPins,
} from "./desktop-pins-store";

/** mirror → owner: pin this frame (its project, display and type). */
export const DESKTOP_PIN_REQUEST_EVENT = "denker://desktop-pin-request";

export interface DesktopPinRequest {
  frameId: string;
  projectId: string;
  /** The display it lands on: its Focus window's, else the asking window's. */
  displayId: number | null;
  /** A point on that display (global logical points), for an owner that
   *  has not heard its work area yet; null when unknown. */
  anchor: { x: number; y: number } | null;
  /** Its type, which decides the widget's sizes (plan 12 §8). */
  frameType: FrameType;
  /** The size an agent asked for; one its type does not allow starts at
   *  the type's starting size (`pinStartSize`). */
  size?: WidgetSize | null;
}

/** The display of the frame's Focus window: floating on its own, or in focus. */
function focusDisplayOf(frameId: string): number | null {
  const floating = useFloatingFrameStore.getState().frames[frameId];
  if (floating) return floating.displayId;
  const presentation = usePresentationStore.getState().presentation;
  return presentation?.frameIds.includes(frameId)
    ? (presentation.displayId ?? null)
    : null;
}

/** The frame leaves Focus (or the preview): its window closes. */
function leaveFocus(frameId: string): void {
  if (useFloatingFrameStore.getState().frames[frameId]) {
    closeFloatingFrame(frameId);
  }
  leavePreview([frameId]);
}

/** How long the asking window waits for the owner to record the pin. */
const PIN_PLACED_WAIT_MS = 5000;

/**
 * The frame leaves Focus once the owner recorded its pin, in this window or
 * another; a pin the owner could not place (no work area) leaves it there.
 */
function leaveFocusOncePinned(frameId: string): void {
  const stop = subscribeDesktopPins(() => {
    if (!desktopPinFor(frameId)) return;
    finish();
    leaveFocus(frameId);
  });
  const timer = setTimeout(finish, PIN_PLACED_WAIT_MS);
  function finish() {
    clearTimeout(timer);
    stop();
  }
}

const isOwner = () => getWindowRoleSync() === "owner";

/**
 * Owner: the widget at its type's starting size, or the size asked when
 * the type allows it (sized freely from there for an editor), in the first
 * free spot on its display, recorded; the
 * owner's windows open it. It keeps clear of Apple's widgets and the icons
 * (Rust) and of every other pin by its record, so a pin whose window is not
 * up yet is never covered either.
 */
export async function placeDesktopPin(
  request: DesktopPinRequest,
): Promise<void> {
  if (typeof request.frameType !== "string") return;
  const sizing = widgetSizingFor(request.frameType);
  const size = pinStartSize(sizing, request.size);
  const [workArea, occupied] = await Promise.all([
    pinWorkArea(request.displayId, request.anchor),
    desktopOccupiedRects().catch(() => []),
  ]);
  if (!workArea) return;
  const pins = readDesktopPins()
    .filter((pin) => pin.frameId !== request.frameId)
    .map((pin) => pin.rect);
  const desktop = occupied.filter((rect) => rect.kind !== "pin");
  putDesktopPin({
    frameId: request.frameId,
    projectId: request.projectId,
    displayId: request.displayId,
    size: sizing.sizes === "free" ? "free" : size,
    rect: firstFreeSpot(WIDGET_SIZES[size], workArea, [...desktop, ...pins]),
  });
}

/** The centre of display `displayId` as this window knows it, else of this
 *  window itself when it is on that display. */
function displayAnchor(
  displayId: number | null,
): { x: number; y: number } | null {
  const area =
    displayId === null ? undefined : getFloatingFrameDisplayArea(displayId);
  if (area) return { x: area.x + area.width / 2, y: area.y + area.height / 2 };
  const origin = getWindowOrigin();
  if (!origin.ready || displayId !== getWindowDisplayId()) return null;
  return {
    x: origin.x + window.innerWidth / 2,
    y: origin.y + window.innerHeight / 2,
  };
}

/**
 * The project a wall frame lives in: the current one for the frames store's
 * (whose `spaceId` may hold the canvas space), the listing's for a card of
 * another project that Everything shows.
 */
function wallFrameProjectId(frameId: string): string | undefined {
  const foreign = foreignWallFrame(frameId);
  if (foreign) return foreign.spaceId;
  return useProjectStore.getState().currentProject?.id;
}

/** The frame leaves Focus and lands on the desktop layer of its display. */
function requestDesktopPin(request: Omit<DesktopPinRequest, "anchor">): void {
  const placed: DesktopPinRequest = {
    ...request,
    anchor: displayAnchor(request.displayId),
  };
  leaveFocusOncePinned(request.frameId);
  if (isOwner()) void placeDesktopPin(placed);
  else void emit(DESKTOP_PIN_REQUEST_EVENT, placed).catch(() => {});
}

/** Pin to desktop: on the display of its Focus window, else of this window. */
export function pinToDesktop(frameId: string): void {
  const frame = wallFrame(frameId);
  const projectId = wallFrameProjectId(frameId);
  if (!frame || !projectId) return;
  requestDesktopPin({
    frameId,
    projectId,
    displayId: focusDisplayOf(frameId) ?? getWindowDisplayId(),
    frameType: frame.frameType,
  });
}

/**
 * An agent's pin (`desktop-pin-agent-request.ts`): on the display it named
 * when that display is known here, else on its Focus window's, else on the
 * display the user works on.
 */
export function pinToDesktopForAgent(
  request: Omit<DesktopPinRequest, "anchor">,
): void {
  const named =
    request.displayId !== null &&
    getFloatingFrameDisplayArea(request.displayId) !== undefined
      ? request.displayId
      : null;
  requestDesktopPin({
    ...request,
    displayId:
      named ??
      focusDisplayOf(request.frameId) ??
      useActiveDisplay.getState().activeDisplayId ??
      getWindowDisplayId(),
  });
}

/** Unpin from desktop: the record goes and its window closes. */
export function unpinFromDesktop(frameId: string): void {
  if (isOwner()) {
    removeDesktopPin(frameId);
    return;
  }
  const payload: DesktopPinIdPayload = { frameId };
  void emit(DESKTOP_PIN_EVENTS.unpin, payload).catch(() => {});
}

/** Owner: the pinned frame opens in Focus, in its own project, on its own display. */
export function showDesktopPinInFocus(frameId: string): void {
  const pin = desktopPinFor(frameId);
  if (!pin) return;
  const { projects } = useProjectStore.getState();
  void openFrameInProject(
    {
      id: frameId,
      projectId: pin.projectId,
      projectName:
        projects.find((project) => project.id === pin.projectId)?.name ?? null,
    },
    {
      focus: true,
      displayId: pin.displayId ?? pinDisplayIdAt(pin.rect, null) ?? undefined,
    },
  );
}

/** Open, from a pin window's ⋯ or a click on its face (or the owner itself). */
export function openDesktopPinInFocus(frameId: string): void {
  if (isOwner()) {
    showDesktopPinInFocus(frameId);
    return;
  }
  const payload: DesktopPinIdPayload = { frameId };
  void emit(DESKTOP_PIN_EVENTS.open, payload).catch(() => {});
}

/** mirror → owner: a pinned frame moved to another project. */
export const DESKTOP_PIN_RETARGET_EVENT = "denker://desktop-pin-retarget";

export interface DesktopPinRetarget {
  frameId: string;
  projectId: string;
}

/** Owner: the pin follows its frame to another project. */
export function retargetDesktopPinRecord({
  frameId,
  projectId,
}: DesktopPinRetarget): void {
  const pin = desktopPinFor(frameId);
  if (pin && pin.projectId !== projectId) putDesktopPin({ ...pin, projectId });
}

/** A frame moved to another project: if it is pinned, its widget follows. */
export function retargetDesktopPin(frameId: string, projectId: string): void {
  if (!isTauri()) return;
  const request: DesktopPinRetarget = { frameId, projectId };
  if (isOwner()) retargetDesktopPinRecord(request);
  else void emit(DESKTOP_PIN_RETARGET_EVENT, request).catch(() => {});
}
